import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, Users, Truck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export const Pricing = () => {
  const [billingInterval, setBillingInterval] = useState('monthly'); // monthly or yearly
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSelectPlan = async (planKey) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/billing/create-checkout-session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan: planKey,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    }
  };

  const plans = [
    {
      name: 'Free',
      tier: 'free',
      price: '£0',
      interval: 'forever',
      icon: Sparkles,
      description: 'Perfect for getting started',
      features: [
        '1 vehicle',
        '3 AI document scans/month',
        'Basic reminders',
        'Expense tracking',
        'Mileage logging',
        'Document storage'
      ],
      limitations: [
        'Limited to 1 vehicle',
        'Only 3 AI scans per month'
      ],
      cta: user ? 'Current Plan' : 'Get Started',
      planKey: null,
      highlighted: false,
      color: 'hsl(var(--muted-foreground))'
    },
    {
      name: 'Premium',
      tier: 'premium',
      price: billingInterval === 'monthly' ? '£4.99' : '£49',
      interval: billingInterval === 'monthly' ? '/month' : '/year',
      savings: billingInterval === 'yearly' ? 'Save £9.88/year' : null,
      icon: Zap,
      description: 'Unlimited power for your vehicle',
      features: [
        '1 vehicle',
        'Unlimited AI scans',
        'Unlimited documents',
        'Advanced reminders',
        'Expense analytics',
        'Mileage trends',
        'Accident reporting',
        'PDF exports',
        'Priority support'
      ],
      limitations: [],
      cta: 'Upgrade to Premium',
      planKey: billingInterval === 'monthly' ? 'premium_monthly' : 'premium_yearly',
      highlighted: true,
      color: 'hsl(var(--brand-sky))'
    },
    {
      name: 'Family',
      tier: 'family',
      price: '£7.99',
      interval: '/month',
      icon: Users,
      description: 'Manage multiple vehicles together',
      features: [
        'Up to 5 vehicles',
        '2-5 user accounts',
        'Unlimited AI scans',
        'Unlimited documents',
        'Shared reminders',
        'Family expense tracking',
        'Multi-vehicle analytics',
        'PDF exports',
        'Priority support'
      ],
      limitations: [],
      cta: 'Upgrade to Family',
      planKey: 'family_monthly',
      highlighted: false,
      color: 'hsl(var(--brand-teal))'
    },
    {
      name: 'Fleet',
      tier: 'fleet',
      price: '£49.99',
      interval: '/month',
      icon: Truck,
      description: 'For small businesses & fleets',
      features: [
        '5-20 vehicles',
        '2-20 user accounts',
        'Unlimited AI scans',
        'Unlimited documents',
        'Fleet-wide analytics',
        'VAT reporting',
        'Custom reminders',
        'Bulk PDF exports',
        'Dedicated support',
        'API access (coming soon)'
      ],
      limitations: [],
      cta: 'Upgrade to Fleet',
      planKey: 'fleet_monthly',
      highlighted: false,
      color: 'hsl(var(--brand-coral))'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-card border-b border-border sticky top-0 z-30 backdrop-blur bg-card/95"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold">Pricing Plans</h1>
            <p className="text-sm text-muted-foreground mt-1">Choose the perfect plan for your needs</p>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Billing Toggle (Premium only) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-2 p-1 rounded-xl bg-secondary border border-border">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                billingInterval === 'monthly'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="billing-toggle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('yearly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                billingInterval === 'yearly'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="billing-toggle-yearly"
            >
              Yearly
              <Badge variant="secondary" className="ml-2 bg-green-500/10 text-green-600 border-green-500/20">
                Save 17%
              </Badge>
            </button>
          </div>
        </motion.div>

        {/* Plan Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          data-testid="pricing-plans-grid"
        >
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isCurrentPlan = user?.subscription?.tier === plan.tier;

            return (
              <motion.div key={plan.tier} variants={cardVariants}>
                <Card
                  className={`relative p-6 flex flex-col h-full ${
                    plan.highlighted
                      ? 'border-2 border-primary shadow-lg card-hover'
                      : 'border border-border card-hover'
                  }`}
                  data-testid={`pricing-plan-${plan.tier}`}
                >
                  {plan.highlighted && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
                      Most Popular
                    </Badge>
                  )}

                  {isCurrentPlan && (
                    <Badge className="absolute -top-3 right-4 bg-green-500 text-white">
                      Current
                    </Badge>
                  )}

                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{
                      background: `linear-gradient(135deg, ${plan.color} 0%, ${plan.color} 100%)`,
                      opacity: 0.15
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: plan.color }} />
                  </div>

                  {/* Plan Name */}
                  <h3 className="text-xl font-semibold mb-1">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold tracking-tight">{plan.price}</span>
                      <span className="text-muted-foreground">{plan.interval}</span>
                    </div>
                    {plan.savings && (
                      <p className="text-xs text-green-600 mt-1 font-medium">{plan.savings}</p>
                    )}
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={() => plan.planKey && handleSelectPlan(plan.planKey)}
                    disabled={!plan.planKey || isCurrentPlan}
                    variant={plan.highlighted ? 'default' : 'outline'}
                    className="w-full mb-6"
                    data-testid={`pricing-plan-${plan.tier}-cta`}
                  >
                    {isCurrentPlan ? 'Current Plan' : plan.cta}
                  </Button>

                  {/* Features */}
                  <div className="space-y-3 flex-1">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* FAQ / Help Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-muted-foreground text-sm">
            Need help choosing? <a href="mailto:hello@vehiclebuddy.co.uk" className="text-primary hover:underline">Contact us</a>
          </p>
          <p className="text-muted-foreground text-xs mt-2">
            All plans include secure data storage and can be cancelled anytime.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing;
